import { useLayoutEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { styled } from "@mui/material/styles";

/** Smallest font multiplier before a line gives up shrinking and wraps instead. */
const MIN_FIT = 0.55;

const Line = styled(Box)({
  whiteSpace: "nowrap",
  minWidth: 0,
  // Past the smallest size the line wraps: text is never cut short.
  "&[data-wrap]": { whiteSpace: "normal", flexWrap: "wrap" },
});

/** Shrinks ``--fit`` (the multiplier its text's font sizes use) until the line's content fits its width. */
function fitToWidth(line: HTMLElement) {
  line.removeAttribute("data-wrap");
  line.style.setProperty("--fit", "1");
  const available = line.clientWidth;
  if (available <= 0 || line.scrollWidth <= available) return;
  let fit = Math.max(MIN_FIT, (available / line.scrollWidth) * 0.99);
  line.style.setProperty("--fit", String(fit));
  // Margins and gaps do not scale with the font, so the first guess can be a little large.
  while (line.scrollWidth > available && fit > MIN_FIT) {
    fit = Math.max(MIN_FIT, fit - 0.03);
    line.style.setProperty("--fit", String(fit));
  }
  if (line.scrollWidth > available) line.setAttribute("data-wrap", "");
}

/**
 * One row of text that is never truncated. Its children size their fonts as `calc(<size> * var(--fit, 1))`;
 * this shrinks `--fit` until the row fits the card's width, and wraps it only below the smallest size.
 */
export function FitLine(props: { children: ReactNode; sx?: SxProps<Theme>; title?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const lastWidth = useRef(-1);

  // After every render: the text may have changed.
  useLayoutEffect(() => {
    if (ref.current) fitToWidth(ref.current);
  });

  useLayoutEffect(() => {
    const line = ref.current;
    if (!line) return undefined;
    // Only a change of width matters; refitting changes the height, which must not start another round.
    const observer = new ResizeObserver(() => {
      if (line.clientWidth === lastWidth.current) return;
      lastWidth.current = line.clientWidth;
      fitToWidth(line);
    });
    observer.observe(line);
    return () => observer.disconnect();
  }, []);

  return (
    <Line ref={ref} sx={props.sx} title={props.title}>
      {props.children}
    </Line>
  );
}
